import type { CSSProperties } from 'react';
import type { NotificationDotProps } from '../../types/notification-dot.type.ts';

const DEFAULT_COLOR = '#dc2626';
const DEFAULT_WAVE_COLOR = '#ef4444';
const DEFAULT_RING_COLOR = '#ffffff';
const DEFAULT_SIZE = 8;
const DEFAULT_WAVE_INSET = 2;
const DEFAULT_SPREAD = 0.3;
const DEFAULT_DURATION = 1000;
const DEFAULT_WAVE_OPACITY = 0.8;
const DEFAULT_PULSE_OPACITY = 0.5;
const DEFAULT_RING_WIDTH = 2;
const DEFAULT_EASING = 'cubic-bezier(0, 0, 0.2, 1)';
const SIDES = 2;

function clampOpacity(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}

export function NotificationDot({
  // 가운데 고정 점의 색상
  color = DEFAULT_COLOR,
  // 바깥으로 퍼지는 물결 애니메이션의 색상
  waveColor = DEFAULT_WAVE_COLOR,
  // 가운데 점을 감싸는 테두리 색상
  ringColor = DEFAULT_RING_COLOR,
  // 가운데 점의 가로·세로 크기(px)
  size = DEFAULT_SIZE,
  // 물결이 가운데 점보다 바깥에서 시작하는 간격(px)
  waveInset = DEFAULT_WAVE_INSET,
  // 물결이 시작 위치에서 추가로 퍼지는 거리(px)
  spread = DEFAULT_SPREAD,
  // 물결과 점멸 애니메이션의 한 주기(ms)
  duration = DEFAULT_DURATION,
  // 애니메이션 시작 지연 시간(ms)
  delay = 0,
  // 물결이 시작할 때의 불투명도(0~1)
  waveOpacity = DEFAULT_WAVE_OPACITY,
  // 가운데 점이 점멸할 때 적용되는 불투명도(0~1)
  pulseOpacity = DEFAULT_PULSE_OPACITY,
  // 가운데 점을 감싸는 테두리 두께(px)
  ringWidth = DEFAULT_RING_WIDTH,
  // 물결과 점멸 애니메이션 전체 사용 여부
  animated = true,
  // animated가 활성화됐을 때 가운데 점의 점멸 사용 여부
  pulse = true,
  // 애니메이션 속도 변화 방식(CSS animation-timing-function)
  easing = DEFAULT_EASING,
  // 위치나 여백 등 추가 Tailwind/CSS 클래스
  className = '',
  // 추가 인라인 스타일
  style,
  // 제공하면 스크린 리더에 알림 의미를 전달하고, 없으면 장식 요소로 처리
  'aria-label': ariaLabel,
  // 그 밖의 표준 span 속성
  ...props
}: NotificationDotProps) {
  const safeSize = Math.max(size, 1);
  const safeWaveInset = Math.max(waveInset, 0);
  const safeSpread = Math.max(spread, 0);
  const safeDuration = Math.max(duration, 1);
  const safeRingWidth = Math.max(ringWidth, 0);
  const waveBaseSize = safeSize + safeWaveInset * SIDES;
  const waveScale =
    (waveBaseSize + safeSpread * SIDES) / waveBaseSize;

  const dotStyle = {
    width: safeSize,
    height: safeSize,
    '--notification-dot-wave-scale': waveScale,
    '--notification-dot-wave-opacity': clampOpacity(waveOpacity),
    '--notification-dot-pulse-opacity': clampOpacity(pulseOpacity),
    ...style,
  } as CSSProperties;

  const animationStyle: CSSProperties = {
    animationDuration: `${safeDuration}ms`,
    animationDelay: `${delay}ms`,
    animationTimingFunction: easing,
    animationIterationCount: 'infinite',
  };

  return (
    <span
      {...props}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      className={`relative inline-flex shrink-0 ${className}`}
      style={dotStyle}
    >
      {animated ? (
        <span
          className="pointer-events-none absolute rounded-full"
          style={{
            ...animationStyle,
            animationName: 'platform-notification-dot-wave',
            backgroundColor: waveColor,
            inset: -safeWaveInset,
          }}
        />
      ) : null}
      <span
        className="relative h-full w-full rounded-full"
        style={{
          ...(animated && pulse
            ? {
                ...animationStyle,
                animationName: 'platform-notification-dot-pulse',
              }
            : undefined),
          backgroundColor: color,
          boxShadow:
            safeRingWidth > 0
              ? `0 0 0 ${safeRingWidth}px ${ringColor}`
              : undefined,
        }}
      />
    </span>
  );
}
